import { useState } from 'react';
import { parseMoney, formatMoneyInput, cleanMoneyTyping } from '../../utils/moneyUtils';
import { calculateDiscount, isNoExpiry, totalProceduresValue } from './packageFormat';

const EMPTY_FORM = {
  name: '',
  description: '',
  price: '',
  validity_days: '',
  active: true,
  procedures: [], // [{ procedure_id, quantity }]
};

const REQUIRED_VALIDITY = 'A validade é obrigatória';
const NEGATIVE_VALIDITY = 'A validade deve ser maior ou igual a zero';

/**
 * Estado e validação do formulário de pacote. formData.price guarda o número em
 * texto com ponto ("1200.99"), como sempre foi para a API; priceText é o que a
 * pessoa vê e digita ("1.200,99"). O payload enviado ao salvar não mudou.
 */
function usePackageForm(procedures) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingPackage, setEditingPackage] = useState(null);
  const [priceText, setPriceText] = useState('');
  const [priceError, setPriceError] = useState('');
  const [validityError, setValidityError] = useState('');
  const [semValidade, setSemValidade] = useState(false);
  const [totalValue, setTotalValue] = useState(0);
  const [discount, setDiscount] = useState({ difference: 0, percentage: 0 });

  const reset = (pkg = null) => {
    setPriceError('');
    setValidityError('');

    if (!pkg) {
      setEditingPackage(null);
      setSemValidade(false);
      setFormData(EMPTY_FORM);
      setPriceText('');
      setTotalValue(0);
      setDiscount({ difference: 0, percentage: 0 });
      return;
    }

    const packageProcedures = pkg.procedures || [];
    const total = totalProceduresValue(packageProcedures, procedures);
    // Sem validade gravada (NULL ou 0) é "Não expira"
    const noExpiry = isNoExpiry(pkg.validity_days);
    const hasValidity = pkg.validity_days !== undefined && !noExpiry;

    setEditingPackage(pkg);
    setTotalValue(total);
    setSemValidade(noExpiry);
    setFormData({
      name: pkg.name,
      description: pkg.description,
      price: pkg.price?.toString() || '',
      validity_days: noExpiry ? '0' : (hasValidity ? pkg.validity_days.toString() : ''),
      active: pkg.active,
      procedures: packageProcedures,
    });
    setPriceText(pkg.price !== undefined && pkg.price !== null && pkg.price !== '' ? formatMoneyInput(pkg.price) : '');
    if (pkg.price) setDiscount(calculateDiscount(total, pkg.price));
  };

  const setProcedureQuantity = (procedure, quantity) => {
    setFormData((prev) => {
      const next = [...prev.procedures];
      // eslint-disable-next-line eqeqeq
      const index = next.findIndex((p) => p.procedure_id == procedure.Id);

      if (quantity <= 0) {
        if (index !== -1) next.splice(index, 1);
      } else if (index !== -1) {
        next[index] = { procedure_id: procedure.Id, quantity };
      } else {
        next.push({ procedure_id: procedure.Id, quantity });
      }

      const newTotal = totalProceduresValue(next, procedures);
      setTotalValue(newTotal);
      if (prev.price) setDiscount(calculateDiscount(newTotal, parseFloat(prev.price)));

      return { ...prev, procedures: next };
    });
  };

  const setActive = (active) => setFormData((prev) => ({ ...prev, active }));

  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const setNoExpiry = (checked) => {
    setSemValidade(checked);
    setFormData((prev) => ({ ...prev, validity_days: checked ? '0' : '' }));
    setValidityError(checked ? '' : REQUIRED_VALIDITY);
  };

  const setValidity = (value) => {
    setFormData((prev) => ({ ...prev, validity_days: value }));
    if (value === '' || isNaN(parseInt(value, 10))) {
      setValidityError(REQUIRED_VALIDITY);
    } else if (parseInt(value, 10) < 0) {
      setValidityError(NEGATIVE_VALIDITY);
    } else {
      setValidityError('');
      // Validade 0 liga o "Não expira" sozinho, e um número maior desliga
      if (parseInt(value, 10) === 0 && !semValidade) setSemValidade(true);
      else if (parseInt(value, 10) > 0 && semValidade) setSemValidade(false);
    }
  };

  const validateValidityOnBlur = () => {
    if (semValidade) return;
    if (formData.validity_days === '' || isNaN(parseInt(formData.validity_days, 10))) {
      setValidityError(REQUIRED_VALIDITY);
    } else if (parseInt(formData.validity_days, 10) < 0) {
      setValidityError(NEGATIVE_VALIDITY);
    }
  };

  // O campo aceita "1.200,99", "1200,99" e "1200.99"; o formulário guarda o número
  const setPrice = (rawText) => {
    const text = cleanMoneyTyping(rawText);
    const parsed = text === '' ? null : parseMoney(text);
    setPriceText(text);

    if (text === '') {
      setField('price', '');
      setPriceError('O preço é obrigatório');
      setDiscount(calculateDiscount(totalValue, NaN));
      return;
    }
    if (parsed === null) {
      setField('price', '');
      setPriceError('Use o formato 1.200,99');
      setDiscount(calculateDiscount(totalValue, NaN));
      return;
    }
    setField('price', String(parsed));
    setPriceError('');
    setDiscount(calculateDiscount(totalValue, parsed));
  };

  const formatPriceOnBlur = () => {
    if (!formData.price) {
      if (!priceError) setPriceError('O preço é obrigatório');
      return;
    }
    setPriceText(formatMoneyInput(formData.price));
  };

  /** Valida e devolve o payload de sempre ({ ...formData, price: número, validity_days: inteiro }) ou null. */
  const buildPayload = () => {
    if (!String(formData.name || '').trim()) return null;
    if (!formData.price || parseFloat(formData.price) < 0) {
      setPriceError('O preço é obrigatório e não pode ser negativo');
      return null;
    }
    if (formData.validity_days === '' || isNaN(parseInt(formData.validity_days, 10))) {
      setValidityError(REQUIRED_VALIDITY);
      return null;
    }
    if (parseInt(formData.validity_days, 10) < 0) {
      setValidityError(NEGATIVE_VALIDITY);
      return null;
    }
    return {
      ...formData,
      price: parseFloat(formData.price),
      validity_days: parseInt(formData.validity_days, 10),
    };
  };

  // O que ainda falta para salvar, na ordem dos blocos do formulário (vazio = pode salvar)
  const missing = [
    !String(formData.name || '').trim() && 'o nome',
    !formData.procedures.some((proc) => proc.quantity > 0) && 'ao menos uma sessão',
    (!!validityError || !formData.validity_days || !(parseInt(formData.validity_days, 10) >= 0)) && 'a validade',
    (!!priceError || !formData.price || !(parseFloat(formData.price) >= 0)) && 'o preço',
  ].filter(Boolean);
  const canSave = missing.length === 0;

  return {
    formData,
    editingPackage,
    priceText,
    priceError,
    validityError,
    semValidade,
    totalValue,
    discount,
    canSave,
    missing,
    reset,
    setField,
    setActive,
    setNoExpiry,
    setValidity,
    validateValidityOnBlur,
    setPrice,
    formatPriceOnBlur,
    setProcedureQuantity,
    buildPayload,
  };
}

export default usePackageForm;
